import LogRocket from 'logrocket';
import { maskUrl } from '@/lib/urlMasking';
import { redactPhi } from '@/lib/phiRedaction';
import * as Sentry from '@sentry/vue';
import { setReplaySink } from '@/lib/logger';

const LOGROCKET_APP_ID = import.meta.env.VITE_LOGROCKET_APP_ID;

export const initLogRocket = () => {
  // Console capture is off so replays get only our scoped, redacted lines
  // (the logger forwards them via setReplaySink), not raw console output.
  LogRocket.init(LOGROCKET_APP_ID, {
    release: import.meta.env.VITE_SENTRY_RELEASE || null,
    shouldDebugLog: false,
    console: { isEnabled: false },
    browser: {
      // the sanitized URL is what LogRocket indexes for dashboard search and metrics
      urlSanitizer: maskUrl
    },
    dom: {
      // Obfuscate every input and select rather than annotating ~460 of them: the patient
      // form alone types MRN, names, DOB and free-text notes. Opt an input back in with
      // `data-public` only when its value is certainly not patient data.
      inputSanitizer: true,
      // inputSanitizer is documented for inputs and selects, so textareas are covered
      // explicitly — family note, room note and the two Aimee memory fields are textareas.
      // `.phi-popper` is for tooltips that carry patient data: they teleport out of the
      // element, so a data-private on the trigger does not reach them.
      redactSelectors: ['textarea', '.phi-popper']
    },
    network: {
      // bodies are redacted rather than dropped, so failed and slow requests stay visible.
      // browser.urlSanitizer does not apply here — request URLs are masked explicitly.
      requestSanitizer: request => ({
        ...request,
        url: maskUrl(request.url),
        body: redactPhi(request.body, request.url)
      }),
      responseSanitizer: response => ({
        ...response,
        url: response.url ? maskUrl(response.url) : response.url,
        body: redactPhi(response.body, response.url)
      })
    }
  });
  setReplaySink(LogRocket);

  // Attach the replay URL to every Sentry event so an error links straight to
  // its LogRocket session. Safe no-op when Sentry isn't initialized.
  LogRocket.getSessionURL((sessionURL) => {
    Sentry.setContext('logrocket', { sessionURL });
  });
}
